import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Ban,
  Handshake,
  Building,
  MapPin,
  Mail,
  Phone,
  User,
  Calendar,
  Send,
  Sparkles,
  Edit2,
  Save,
  X,
  Clock,
  MessageSquare,
  CheckCircle,
  Reply,
  Paperclip,
  Plus,
  Trash2,
  Upload,
  Image,
  Bot,
  Loader2,
  Zap,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Users,
  Target,
  Activity,
  Lightbulb,
  Shield,
  ArrowRight,
  ThumbsUp,
  Search,
} from "lucide-react";
import { API, STAGES, TIERS, SECTORS, CONFIDENCE_LEVELS, ENGAGEMENT_TYPES, CURRENCIES, REVENUE_REQUIRED_STAGES, SIGNED_REVENUE_STAGES } from "@/lib/constants";
import { OpportunitiesSection } from "@/components/OpportunitiesSection";
import RelationshipHeadline from "@/components/RelationshipHeadline";
import { DocumentsSection } from "@/components/DocumentsSection";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export default function ClientDetail({ user }) {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get("tab") || "details";
  const focusOppId = searchParams.get("focus") || null;

  const [client, setClient] = useState(null);
  const [engagements, setEngagements] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState(null);

  // Email state
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [toEmail, setToEmail] = useState("");
  const [toName, setToName] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);

  // AI Reply state
  const [clientResponse, setClientResponse] = useState("");
  const [generatingReply, setGeneratingReply] = useState(false);
  const [suggestedAttachments, setSuggestedAttachments] = useState([]);

  // Contact management state
  const [showAddContact, setShowAddContact] = useState(false);
  const [newContact, setNewContact] = useState({ name: "", email: "", phone: "", title: "" });
  const [savingContact, setSavingContact] = useState(false);
  const [editingContactIdx, setEditingContactIdx] = useState(null);
  const [editContactData, setEditContactData] = useState({ name: "", email: "", phone: "", title: "" });

  // Users list for ownership change
  const [users, setUsers] = useState([]);

  // Logo upload state
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  
  // Rex insights state
  const [showRexPanel, setShowRexPanel] = useState(false);
  const [togglingRestricted, setTogglingRestricted] = useState(false);
  // The user's explicit restricted choice is authoritative and must survive a slow in-flight
  // page-load fetch resolving afterwards (which would otherwise revert the optimistic value).
  const restrictedAuthRef = useRef(null);
  const [togglingPartner, setTogglingPartner] = useState(false);
  // Partner is a coexisting attribute (item 23); same slow-load protection as restricted.
  const partnerAuthRef = useRef(null);
  const [rexInsights, setRexInsights] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);

  // Relationship Memory state (Phase 2)
  const [relationshipInsight, setRelationshipInsight] = useState(null);
  const [loadingRelationship, setLoadingRelationship] = useState(false);
  const [interactionText, setInteractionText] = useState("");
  const [loggingInteraction, setLoggingInteraction] = useState(false);

  // Sales Guidance state (Phase 3)
  const [salesGuidance, setSalesGuidance] = useState(null);
  const [loadingGuidance, setLoadingGuidance] = useState(false);
  const [showBriefing, setShowBriefing] = useState(true);

  // Opportunities state
  const [opportunities, setOpportunities] = useState([]);
  const [showAddOpp, setShowAddOpp] = useState(false);
  // N3 Commit B — arriving from the Weekly BD Playbook "Create opportunity" offer opens the add dialog.
  useEffect(() => {
    if (searchParams.get("new") === "opp") setShowAddOpp(true);
  }, [searchParams]);
  const [newOpp, setNewOpp] = useState({ opportunity_name: "", engagement_type: "Commercial Opportunity", stage: "Unqualified Leads", opportunity_value: "", opportunity_currency: "NGN", expected_close_date: "", deal_owner_id: "", deal_owner_name: "", account_manager_id: "", account_manager_name: "" });
  const [editingOppId, setEditingOppId] = useState(null);
  const [editOppData, setEditOppData] = useState({});
  const [showAddRevEntry, setShowAddRevEntry] = useState(null); // opp_id to show form
  const [newRevEntry, setNewRevEntry] = useState({ amount: "", currency: "NGN", scheduled_date: "", status: "Forecast", category: "Milestone", description: "" });

  // Notes state
  const [clientNotes, setClientNotes] = useState([]);
  const [newClientNote, setNewClientNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editNoteContent, setEditNoteContent] = useState("");
  // Structured conversation logging (into the same Notes tab)
  const [showConvForm, setShowConvForm] = useState(false);
  const [savingConv, setSavingConv] = useState(false);
  const [noteFilter, setNoteFilter] = useState("");
  const emptyConv = { subject: "", conversation_date: new Date().toISOString().slice(0, 10), conversation_time: "", participants: "", agenda: "", key_points: "", summary: "", content: "" };
  const [conv, setConv] = useState(emptyConv);

  // Documents state
  const [clientDocuments, setClientDocuments] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);

  // Check if user can edit
  const canEdit = user?.role === "admin" || user?.role === "user";

  useEffect(() => {
    fetchClientData();
    fetchTemplates();
    fetchUsers();
    fetchRelationshipInsight();
    fetchSalesGuidance();
    fetchOpportunities();
    fetchClientNotes();
    fetchClientDocuments();
  }, [clientId]);

  // Unified refresh for all client page data after any mutation
  const refreshAllClientData = () => {
    fetchClientData();
    fetchRelationshipInsight();
    fetchSalesGuidance();
    fetchOpportunities();
    fetchClientNotes();
    fetchClientDocuments();
  };

  const fetchClientNotes = async () => {
    try {
      const response = await fetch(`${API}/notes?parent_id=${clientId}&parent_type=client`, { credentials: "include" });
      if (response.ok) setClientNotes(await response.json());
    } catch (error) {
      console.error("Error fetching notes:", error);
    }
  };

  const fetchClientDocuments = async () => {
    try {
      const response = await fetch(`${API}/client-documents/${clientId}`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setClientDocuments(data.documents || []);
      }
    } catch (error) {
      console.error("Error fetching documents:", error);
    }
  };

  const handleAddClientNote = async () => {
    if (!newClientNote.trim()) return;
    setSavingNote(true);

    // Optimistic UI: add the note to local state immediately
    const optimisticNote = {
      note_id: `temp_${Date.now()}`,
      parent_id: clientId,
      parent_type: "client",
      content: newClientNote.trim(),
      author_id: user?.user_id,
      author_name: user?.name || "You",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      _optimistic: true,
    };
    const previousNotes = clientNotes;
    setClientNotes((prev) => [optimisticNote, ...prev]);
    setNewClientNote("");

    try {
      const res = await fetch(`${API}/notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ parent_id: clientId, parent_type: "client", content: optimisticNote.content }),
      });
      if (res.ok) {
        const savedNote = await res.json();
        // Replace the optimistic note with the real one from the server
        setClientNotes((prev) =>
          prev.map((n) => (n.note_id === optimisticNote.note_id ? { ...savedNote } : n))
        );
        toast.success("Note added");
      } else {
        // Rollback on failure
        setClientNotes(previousNotes);
        setNewClientNote(optimisticNote.content);
        toast.error("Failed to add note");
      }
    } catch {
      // Rollback on network error
      setClientNotes(previousNotes);
      setNewClientNote(optimisticNote.content);
      toast.error("Network error");
    } finally {
      setSavingNote(false);
    }
  };

  const handleAddConversation = async () => {
    if (!conv.subject.trim() && !conv.summary.trim()) {
      toast.error("Add at least a subject or a summary");
      return;
    }
    setSavingConv(true);
    const splitList = (s) => s.split(/[\n,]/).map((x) => x.trim()).filter(Boolean);
    try {
      const res = await fetch(`${API}/notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          parent_id: clientId,
          parent_type: "client",
          note_type: "conversation",
          subject: conv.subject.trim(),
          conversation_date: conv.conversation_date,
          conversation_time: conv.conversation_time,
          participants: splitList(conv.participants),
          agenda: conv.agenda.trim(),
          key_points: splitList(conv.key_points),
          summary: conv.summary.trim(),
          content: conv.content.trim(),
        }),
      });
      if (res.ok) {
        const saved = await res.json();
        setClientNotes((prev) => [saved, ...prev]);
        setConv(emptyConv);
        setShowConvForm(false);
        toast.success("Conversation logged");
      } else {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || "Failed to log conversation");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setSavingConv(false);
    }
  };


  const handleUpdateNote = async (noteId) => {
    try {
      const res = await fetch(`${API}/notes/${noteId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ content: editNoteContent.trim() }),
      });
      if (res.ok) {
        toast.success("Note updated");
        setEditingNoteId(null);
        refreshAllClientData();
      }
    } catch { toast.error("Network error"); }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm("Delete this note?")) return;
    // Optimistic UI: remove note immediately
    const previousNotes = clientNotes;
    setClientNotes((prev) => prev.filter((n) => n.note_id !== noteId));
    try {
      const res = await fetch(`${API}/notes/${noteId}`, { method: "DELETE", credentials: "include" });
      if (res.ok) {
        toast.success("Note deleted");
      } else {
        setClientNotes(previousNotes);
        toast.error("Failed to delete note");
      }
    } catch {
      setClientNotes(previousNotes);
      toast.error("Network error");
    }
  };

  const fetchOpportunities = async () => {
    try {
      const response = await fetch(`${API}/opportunities?client_id=${clientId}`, { credentials: "include" });
      if (response.ok) setOpportunities(await response.json());
    } catch (error) {
      console.error("Error fetching opportunities:", error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API}/users`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  // Phase 2: Fetch Relationship Insight
  const fetchRelationshipInsight = async () => {
    setLoadingRelationship(true);
    try {
      const response = await fetch(`${API}/rex-relationship/client/${clientId}/insight`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setRelationshipInsight(data);
        }
      }
    } catch (error) {
      console.error("Error fetching relationship insight:", error);
    } finally {
      setLoadingRelationship(false);
    }
  };

  // Phase 2: Log Interaction
  const handleLogInteraction = async () => {
    if (!interactionText.trim()) {
      toast.error("Please describe what happened");
      return;
    }
    
    setLoggingInteraction(true);
    try {
      const response = await fetch(`${API}/rex-relationship/log-interaction`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          client_id: clientId,
          update_text: interactionText,
        }),
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          toast.success("Interaction logged and interpreted");
          setInteractionText("");
          refreshAllClientData();
        } else {
          toast.error(result.error || "Failed to log interaction");
        }
      } else {
        toast.error("Failed to log interaction");
      }
    } catch (error) {
      console.error("Error logging interaction:", error);
      toast.error("Failed to log interaction");
    } finally {
      setLoggingInteraction(false);
    }
  };

  // Phase 3: Fetch Sales Guidance
  const [adviceFlag, setAdviceFlag] = useState({ enabled: false, reenable_warning: "" });
  const [flagBusy, setFlagBusy] = useState(false);
  const canToggleAdvice = ["admin", "managing_partner"].includes(user?.role);
  const fetchAdviceFlag = async () => {
    try {
      const r = await fetch(`${API}/settings/legacy-client-advice`, { credentials: "include" });
      if (r.ok) setAdviceFlag(await r.json());
    } catch (e) { /* noop */ }
  };
  const toggleAdvice = async (next) => {
    if (next && !window.confirm(adviceFlag.reenable_warning || "Turn legacy advice back on?")) return;
    setFlagBusy(true);
    try {
      const r = await fetch(`${API}/settings/legacy-client-advice`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: next }),
      });
      if (r.ok) { await fetchAdviceFlag(); fetchSalesGuidance(); fetchRelationshipInsight(); }
      else if (r.status === 403) alert("Only an administrator or managing partner can change this setting.");
    } finally { setFlagBusy(false); }
  };
  const fetchSalesGuidance = async () => {
    setLoadingGuidance(true);
    fetchAdviceFlag();
    try {
      const response = await fetch(`${API}/rex-guidance/client/${clientId}/full`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSalesGuidance(data);
        }
      }
    } catch (error) {
      console.error("Error fetching sales guidance:", error);
    } finally {
      setLoadingGuidance(false);
    }
  };

  // Phase 3: Acknowledge intervention
  const acknowledgeIntervention = async (interventionType) => {
    try {
      await fetch(`${API}/rex-guidance/client/${clientId}/acknowledge-intervention`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ intervention_type: interventionType }),
      });
      // Refresh guidance
      fetchSalesGuidance();
    } catch (error) {
      console.error("Error acknowledging intervention:", error);
    }
  };

  const handleToggleRestricted = async () => {
    const next = !client.restricted;
    let reason = "";
    if (next) {
      reason = window.prompt("Why is this organisation restricted? (recorded with the flag)") || "";
      if (!reason.trim()) return;
    }
    setTogglingRestricted(true);
    try {
      const res = await fetch(`${API}/relationship/clients/${clientId}/restricted`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ restricted: next, reason }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || `HTTP ${res.status}`);
      const data = await res.json().catch(() => ({}));
      const applied = typeof data.restricted === "boolean" ? data.restricted : next;
      restrictedAuthRef.current = applied; // authoritative; wins over any late-resolving load fetch
      setClient((prev) => ({ ...prev, restricted: applied }));
      toast.success(applied ? "Marked restricted — Rex will block all contact" : "Restriction lifted");
    } catch (e) {
      toast.error(`Couldn't update: ${e.message}`);
    } finally {
      setTogglingRestricted(false);
    }
  };

  const handleTogglePartner = async () => {
    const next = !client.partner;
    let reason = "";
    if (next) {
      reason = window.prompt("Why is this organisation a partner? (recorded with the flag)") || "";
      if (!reason.trim()) return;
    }
    setTogglingPartner(true);
    try {
      const res = await fetch(`${API}/relationship/clients/${clientId}/partner`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ partner: next, reason }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || `HTTP ${res.status}`);
      const data = await res.json().catch(() => ({}));
      const applied = typeof data.partner === "boolean" ? data.partner : next;
      partnerAuthRef.current = applied;
      setClient((prev) => ({ ...prev, partner: applied }));
      toast.success(applied ? "Marked as partner" : "Partner mark removed");
    } catch (e) {
      toast.error(`Couldn't update: ${e.message}`);
    } finally {
      setTogglingPartner(false);
    }
  };

  const fetchClientData = async () => {
    try {
      const [clientRes, engagementsRes] = await Promise.all([
        fetch(`${API}/clients/${clientId}`, { credentials: "include" }),
        fetch(`${API}/engagements/${clientId}`, { credentials: "include" }),
      ]);

      if (clientRes.ok) {
        const clientData = await clientRes.json();
        if (restrictedAuthRef.current !== null) clientData.restricted = restrictedAuthRef.current;
        if (partnerAuthRef.current !== null) clientData.partner = partnerAuthRef.current;
        setClient(clientData);
        setEditData(clientData);
        if (clientData.contacts?.length > 0) {
          setToEmail(clientData.contacts[0].email || "");
          setToName(clientData.contacts[0].name || "");
        }
      } else {
        toast.error("Client not found");
        navigate("/clients");
      }

      if (engagementsRes.ok) {
        const engData = await engagementsRes.json();
        setEngagements(engData);
      }
    } catch (error) {
      console.error("Error fetching client:", error);
      toast.error("Failed to load client");
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await fetch(`${API}/templates`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error("Error fetching templates:", error);
    }
  };

  const SIGNED_WON = [...SIGNED_REVENUE_STAGES, "Closed \u2013 Won"];
  const [leaveSignedArmed, setLeaveSignedArmed] = useState(false);
  const [leaveSignedReason, setLeaveSignedReason] = useState("");

  const handleSaveEdit = async () => {
    // S2 item 3 — leaving a signed/won stage needs a confirmation and a reason, recorded
    // in the deal's history via the existing client update path.
    const leavingSigned = SIGNED_WON.includes(client.stage) && !SIGNED_WON.includes(editData.stage);
    if (leavingSigned && !leaveSignedArmed) { setLeaveSignedArmed(true); return; }
    if (leavingSigned && !leaveSignedReason.trim()) { toast.error("A reason is required to move a deal out of a signed/won stage"); return; }
    try {
      const body = { ...editData };
      if (leavingSigned && leaveSignedReason.trim()) body.stage_change_reason = leaveSignedReason.trim();
      const response = await fetch(`${API}/clients/${clientId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });

      if (response.ok) {
        const updated = await response.json();
        setClient(updated);
        setEditData(updated);
        setEditing(false);
        setLeaveSignedArmed(false);
        setLeaveSignedReason("");
        toast.success("Client updated successfully");
        refreshAllClientData();
      } else {
        const err = await response.json().catch(() => null);
        toast.error(err?.detail || "Failed to update client");
      }
    } catch (error) {
      toast.error("Failed to update client");
    }
  };

  const handleAddContact = async () => {
    if (!newContact.name && !newContact.email) {
      toast.error("Please enter at least a name or email");
      return;
    }

    setSavingContact(true);
    try {
      // Add new contact to existing contacts array
      const updatedContacts = [...(client.contacts || []), newContact];
      
      const response = await fetch(`${API}/clients/${clientId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ contacts: updatedContacts }),
      });

      if (response.ok) {
        const updated = await response.json();
        setClient(updated);
        setNewContact({ name: "", email: "", phone: "", title: "" });
        setShowAddContact(false);
        toast.success("Contact added successfully");
        
        // Update email recipient if this is the first contact
        if (updatedContacts.length === 1) {
          setToEmail(newContact.email || "");
          setToName(newContact.name || "");
        }
      } else {
        toast.error("Failed to add contact");
      }
    } catch (error) {
      toast.error("Failed to add contact");
    } finally {
      setSavingContact(false);
    }
  };

  const handleDeleteContact = async (indexToDelete) => {
    try {
      const updatedContacts = client.contacts.filter((_, idx) => idx !== indexToDelete);
      
      const response = await fetch(`${API}/clients/${clientId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ contacts: updatedContacts }),
      });

      if (response.ok) {
        const updated = await response.json();
        setClient(updated);
        toast.success("Contact removed");
      } else {
        toast.error("Failed to remove contact");
      }
    } catch (error) {
      toast.error("Failed to remove contact");
    }
  };

  const handleEditContact = (idx) => {
    setEditingContactIdx(idx);
    setEditContactData({ ...client.contacts[idx] });
  };

  const handleSaveContactEdit = async () => {
    if (!editContactData.name && !editContactData.email) {
      toast.error("Please enter at least a name or email");
      return;
    }

    setSavingContact(true);
    try {
      const updatedContacts = client.contacts.map((contact, idx) =>
        idx === editingContactIdx ? editContactData : contact
      );

      const response = await fetch(`${API}/clients/${clientId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ contacts: updatedContacts }),
      });

      if (response.ok) {
        const updated = await response.json();
        setClient(updated);
        setEditingContactIdx(null);
        setEditContactData({ name: "", email: "", phone: "", title: "" });
        toast.success("Contact updated successfully");
      } else {
        toast.error("Failed to update contact");
      }
    } catch (error) {
      toast.error("Failed to update contact");
    } finally {
      setSavingContact(false);
    }
  };

  const handleOwnerChange = async (newOwnerId) => {
    const selectedUser = users.find(u => u.user_id === newOwnerId);
    if (!selectedUser) return;

    try {
      const response = await fetch(`${API}/clients/${clientId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          account_manager_id: selectedUser.user_id,
          account_manager_name: selectedUser.name,
        }),
      });

      if (response.ok) {
        const updated = await response.json();
        setClient(updated);
        setEditData(updated);
        toast.success(`Ownership changed to ${selectedUser.name}`);
      } else {
        toast.error("Failed to change ownership");
      }
    } catch (error) {
      toast.error("Failed to change ownership");
    }
  };

  const handleMarkResponseReceived = async () => {
    try {
      const response = await fetch(`${API}/clients/${clientId}/response-received`, {
        method: "PUT",
        credentials: "include",
      });

      if (response.ok) {
        setClient((prev) => ({
          ...prev,
          response_received: true,
          sequence_status: "paused",
        }));
        toast.success("Marked as response received - sequence paused");
      } else {
        toast.error("Failed to update");
      }
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplate(templateId);
    const template = templates.find((t) => t.template_id === templateId);
    if (template) {
      let subject = template.subject
        .replace(/\{\{company_name\}\}/g, client?.company_name || "")
        .replace(/\{\{contact_name\}\}/g, toName || "");
      let body = template.body
        .replace(/\{\{company_name\}\}/g, client?.company_name || "")
        .replace(/\{\{contact_name\}\}/g, toName || "")
        .replace(/\{\{sender_name\}\}/g, user?.name || "");

      setEmailSubject(subject);
      setEmailBody(body);
    }
  };

  const handleGenerateAI = async (category) => {
    setGeneratingAI(true);
    try {
      const response = await fetch(`${API}/emails/generate-ai`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          client_id: clientId,
          template_category: category,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        setEmailSubject(result.subject);
        setEmailBody(result.body);
        if (result.suggested_attachments) {
          setSuggestedAttachments(result.suggested_attachments);
        }
        toast.success("AI email generated - you can edit before sending");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to generate email");
      }
    } catch (error) {
      toast.error("Failed to generate AI email");
    } finally {
      setGeneratingAI(false);
    }
  };

  const handleGenerateReply = async () => {
    if (!clientResponse.trim()) {
      toast.error("Please paste the client's response first");
      return;
    }

    setGeneratingReply(true);
    try {
      const response = await fetch(`${API}/emails/generate-reply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          client_id: clientId,
          client_response: clientResponse,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        setEmailSubject(result.subject);
        setEmailBody(result.body);
        if (result.suggested_attachments) {
          setSuggestedAttachments(result.suggested_attachments);
        }
        toast.success("AI reply generated - review and edit before sending");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to generate reply");
      }
    } catch (error) {
      toast.error("Failed to generate AI reply");
    } finally {
      setGeneratingReply(false);
    }
  };

  // Logo upload handlers
  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp", "image/svg+xml"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload a PNG, JPG, WEBP, or SVG file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(`${API}/clients/${clientId}/logo`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        setClient({ ...client, logo_url: result.logo_url });
        toast.success("Logo uploaded successfully");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to upload logo");
      }
    } catch (error) {
      toast.error("Failed to upload logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (!window.confirm("Are you sure you want to remove the client logo?")) {
      return;
    }

    try {
      const response = await fetch(`${API}/clients/${clientId}/logo`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        setClient({ ...client, logo_url: null });
        toast.success("Logo removed");
      } else {
        toast.error("Failed to remove logo");
      }
    } catch (error) {
      toast.error("Failed to remove logo");
    }
  };

  const handleSendEmail = async () => {
    if (!toEmail || !emailSubject || !emailBody) {
      toast.error("Please fill in all email fields");
      return;
    }

    setSendingEmail(true);
    try {
      const response = await fetch(`${API}/emails/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          client_id: clientId,
          template_id: selectedTemplate || null,
          to_email: toEmail,
          to_name: toName,
          subject: emailSubject,
          body: emailBody,
          from_name: user?.name || "RESILIGENT Team",
        }),
      });

      if (response.ok) {
        toast.success("Email sent successfully!");
        const engRes = await fetch(`${API}/engagements/${clientId}`, {
          credentials: "include",
        });
        if (engRes.ok) {
          setEngagements(await engRes.json());
        }
        setEmailSubject("");
        setEmailBody("");
        setSelectedTemplate("");
        setClientResponse("");
        setSuggestedAttachments([]);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to send email");
      }
    } catch (error) {
      toast.error("Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };
  
  // Ask Rex for insights
  const fetchRexInsights = async () => {
    setLoadingInsights(true);
    try {
      const response = await fetch(`${API}/ask-rex/client/${clientId}/quick-insights`, {
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        setRexInsights(data.insights);
        setShowRexPanel(true);
      } else {
        toast.error("Failed to get insights");
      }
    } catch (error) {
      toast.error("Failed to get insights");
    } finally {
      setLoadingInsights(false);
    }
  };

  const getStageColor = (stage) => {
    const found = STAGES.find((s) => s.id === stage);
    return found ? found.color : "bg-slate-500";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Client not found</p>
        <Button onClick={() => navigate("/clients")} className="mt-4">
          Back to Clients
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="client-detail-page">
      {/* Header */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/clients")}
            data-testid="back-to-clients-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="min-w-0">
            <h1 className="text-lg sm:text-2xl font-bold text-slate-900 font-['Manrope'] truncate">
              {client.company_name}
            </h1>
            <div className="flex flex-wrap items-center gap-1.5 mt-1">
              {client.stage && (
                <Badge className={`${getStageColor(client.stage)} text-white text-[10px] sm:text-xs`}>
                  {client.stage}
                </Badge>
              )}
              {client.tier && <Badge variant="outline" className="text-[10px] sm:text-xs">{client.tier}</Badge>}
              {client.response_received && (
                <Badge className="bg-emerald-100 text-emerald-700 text-[10px] sm:text-xs">
                  <CheckCircle className="w-3 h-3 mr-0.5" />
                  Response
                </Badge>
              )}
              {client.sequence_status === "active" && (
                <Badge className="bg-blue-100 text-blue-700 text-[10px] sm:text-xs">
                  In Sequence
                </Badge>
              )}
              {client.restricted && (
                <Badge className="bg-red-600 text-white text-[10px] sm:text-xs" data-testid="client-restricted-badge">
                  <Ban className="w-3 h-3 mr-0.5" /> Restricted — do not contact
                </Badge>
              )}
              {client.partner && (
                <Badge className="bg-violet-600 text-white text-[10px] sm:text-xs" data-testid="client-partner-badge">
                  <Handshake className="w-3 h-3 mr-0.5" /> Partner
                </Badge>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Ask Rex Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={fetchRexInsights}
            disabled={loadingInsights}
            className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
            data-testid="ask-rex-btn"
          >
            {loadingInsights ? (
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            ) : (
              <Bot className="w-4 h-4 mr-1" />
            )}
            Ask Rex
          </Button>
          {["admin", "owner", "managing_partner"].includes(user?.role) && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleToggleRestricted}
              disabled={togglingRestricted}
              className={client.restricted
                ? "text-slate-600 border-slate-200 hover:bg-slate-50"
                : "text-red-600 border-red-200 hover:bg-red-50"}
              data-testid="toggle-restricted-btn"
            >
              {togglingRestricted ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Ban className="w-4 h-4 mr-1" />}
              {client.restricted ? "Lift restriction" : "Mark restricted"}
            </Button>
          )}
          {["admin", "owner", "managing_partner"].includes(user?.role) && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleTogglePartner}
              disabled={togglingPartner}
              className={client.partner
                ? "text-slate-600 border-slate-200 hover:bg-slate-50"
                : "text-violet-600 border-violet-200 hover:bg-violet-50"}
              data-testid="toggle-partner-btn"
            >
              {togglingPartner ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Handshake className="w-4 h-4 mr-1" />}
              {client.partner ? "Remove partner" : "Mark as partner"}
            </Button>
          )}
          {!client.response_received && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleMarkResponseReceived}
              className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
              data-testid="mark-response-btn"
            >
              <CheckCircle className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Response Received</span>
              <span className="sm:hidden">Received</span>
            </Button>
          )}
          {!editing ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditing(true)}
              data-testid="edit-client-btn"
            >
              <Edit2 className="w-4 h-4 mr-1" /> Edit
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setEditing(false);
                  setEditData(client);
                }}
              >
                <X className="w-4 h-4 mr-1" /> Cancel
              </Button>
              <Button
                className="bg-blue-600 hover:bg-blue-700"
                size="sm"
                onClick={handleSaveEdit}
                data-testid="save-client-btn"
              >
                <Save className="w-4 h-4 mr-1" /> Save
              </Button>
            </div>
          )}
        </div>
      </div>
      
      {/* Rex Insights Panel */}
      {showRexPanel && rexInsights && (
        <Card className="bg-indigo-50 border-indigo-200">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2 text-indigo-900">
                <Bot className="w-5 h-5" />
                Rex's Insights on {client.company_name}
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowRexPanel(false)}
                className="h-8 w-8 p-0"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="whitespace-pre-wrap text-sm text-slate-700">
              {rexInsights}
            </div>
            <div className="mt-4 pt-4 border-t border-indigo-200 flex gap-2">
              <Button
                size="sm"
                className="bg-indigo-600 hover:bg-indigo-700"
                onClick={() => {
                  setShowRexPanel(false);
                  navigate(`/proposals?new=true&client_id=${clientId}`);
                }}
              >
                <Zap className="w-4 h-4 mr-2" />
                Create Proposal
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate(`/ask-rex?client=${clientId}`)}
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Chat with Rex
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Release B: rebuild message shown in place of the seven advice panels while off */}
      {(salesGuidance?.suppressed || relationshipInsight?.suppressed) && (
        <Card className="border-slate-300 bg-slate-50" data-testid="advice-rebuild-notice">
          <CardContent className="py-4">
            <p className="text-sm text-slate-700">
              Rex's client advice is being rebuilt because it was not reading emails or payments. The records below are shown as currently recorded.
            </p>
            {canToggleAdvice && (
              <button type="button" disabled={flagBusy} onClick={() => toggleAdvice(true)}
                className="mt-3 text-xs px-3 py-1.5 rounded border border-slate-300 text-slate-600 hover:bg-slate-100"
                data-testid="advice-toggle-on">
                {flagBusy ? "Working…" : "Admin: turn the old advice back on"}
              </button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Sales Guidance Panel (Phase 3) */}
      {!salesGuidance?.suppressed && salesGuidance && (salesGuidance.briefing || salesGuidance.next_action || salesGuidance.active_interventions?.length > 0) && (
        <Card className="border-emerald-200 bg-gradient-to-r from-emerald-50 to-white">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2 text-slate-800">
                <Lightbulb className="w-5 h-5 text-emerald-600" />
                Rex's Guidance
              </CardTitle>
              {loadingGuidance && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Active Interventions (Cooling Deal Alerts) */}
            {salesGuidance.active_interventions?.map((intervention, idx) => (
              <div
                key={idx}
                className="bg-amber-50 border border-amber-200 rounded-lg p-4"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                    <span className="font-medium text-amber-800">
                      {intervention.intervention_type === "cooling_deal"
                        ? "Cooling Deal Alert"
                        : "Attention Required"}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => acknowledgeIntervention(intervention.intervention_type)}
                    className="text-amber-600 hover:text-amber-800"
                  >
                    <ThumbsUp className="w-4 h-4 mr-1" /> Acknowledge
                  </Button>
                </div>
                {intervention.intervention && (
                  <div className="space-y-2 text-sm">
                    <p>
                      <span className="font-medium text-amber-700">What changed: </span>
                      <span className="text-amber-900">{intervention.intervention.what_changed}</span>
                    </p>
                    <p>
                      <span className="font-medium text-amber-700">Likely reason: </span>
                      <span className="text-amber-900">{intervention.intervention.likely_reason}</span>
                    </p>
                    <p>
                      <span className="font-medium text-emerald-700">Re-engagement angle: </span>
                      <span className="text-emerald-900">{intervention.intervention.reengagement_angle}</span>
                    </p>
                    <p>
                      <span className="font-medium text-red-700">Avoid: </span>
                      <span className="text-red-900">{intervention.intervention.what_not_to_do}</span>
                    </p>
                  </div>
                )}
              </div>
            ))}

            {/* Pre-Meeting Briefing */}
            {salesGuidance.briefing && showBriefing && (
              <div className="bg-white rounded-lg p-4 border border-emerald-200">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-emerald-700 uppercase tracking-wide">
                    Pre-Meeting Briefing
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowBriefing(false)}
                    className="text-slate-400 hover:text-slate-600 h-6 w-6 p-0"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                <div className="space-y-3 text-sm">
                  <div>
                    <span className="font-medium text-slate-600">Reality: </span>
                    <span className="text-slate-800">{salesGuidance.briefing.relationship_reality}</span>
                  </div>
                  <div>
                    <span className="font-medium text-slate-600">Their concern: </span>
                    <span className="text-slate-800">{salesGuidance.briefing.likely_concern}</span>
                  </div>
                  <div className="bg-emerald-50 rounded p-2 border border-emerald-100">
                    <span className="font-medium text-emerald-700">Entry angle: </span>
                    <span className="text-emerald-900">{salesGuidance.briefing.entry_angle}</span>
                  </div>
                  <div className="bg-red-50 rounded p-2 border border-red-100">
                    <span className="font-medium text-red-700">Avoid: </span>
                    <span className="text-red-900">{salesGuidance.briefing.avoid}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Next Best Action */}
            {salesGuidance.next_action && (
              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <div className="flex items-center gap-2 mb-3">
                  <Target className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-slate-700">Next Best Action</span>
                  <Badge
                    variant="outline"
                    className={`text-xs ${
                      salesGuidance.next_action.confidence === "high"
                        ? "bg-green-50 text-green-700 border-green-200"
                        : "bg-amber-50 text-amber-700 border-amber-200"
                    }`}
                  >
                    {salesGuidance.next_action.confidence} confidence
                  </Badge>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 border border-blue-100 mb-3">
                  <p className="font-medium text-blue-900">
                    {salesGuidance.next_action.action_description}
                  </p>
                </div>
                {salesGuidance.next_action.reasoning && (
                  <div className="space-y-2 text-xs text-slate-600">
                    <p>
                      <span className="font-medium">Observed: </span>
                      {salesGuidance.next_action.reasoning.observed_state}
                    </p>
                    <p>
                      <span className="font-medium">Situation: </span>
                      {salesGuidance.next_action.reasoning.likely_situation}
                    </p>
                    <p>
                      <span className="font-medium">Why this: </span>
                      {salesGuidance.next_action.reasoning.why_this_action}
                    </p>
                    <p className="text-red-600">
                      <span className="font-medium">Risk to avoid: </span>
                      {salesGuidance.next_action.reasoning.risk_to_avoid}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Stakeholder Gaps */}
            {salesGuidance.stakeholder_gaps?.has_gaps && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="w-4 h-4 text-red-500" />
                  <span className="text-sm font-medium text-red-800">Stakeholder Warning</span>
                </div>
                {salesGuidance.stakeholder_gaps.warnings?.map((warning, idx) => (
                  <div key={idx} className="text-sm mb-2">
                    <p className="text-red-700 font-medium">{warning.message}</p>
                    <p className="text-red-600 text-xs mt-1">{warning.suggestion}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Mispositioning Warnings */}
            {salesGuidance.mispositioning_warnings?.has_risks && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  <span className="text-sm font-medium text-amber-800">Positioning Risk</span>
                </div>
                {salesGuidance.mispositioning_warnings.warnings?.map((warning, idx) => (
                  <p key={idx} className="text-sm text-amber-700">
                    {warning.warning}
                  </p>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Relationship Memory Panel (Phase 2) */}
      {!relationshipInsight?.suppressed && (
      <Card className="border-slate-200 bg-gradient-to-r from-slate-50 to-white">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2 text-slate-800">
              <Activity className="w-5 h-5 text-blue-600" />
              Relationship Intelligence
            </CardTitle>
            {loadingRelationship && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Quick Interaction Log */}
          <div className="bg-white rounded-lg p-3 border border-slate-200">
            <Label className="text-sm font-medium text-slate-600 mb-2 block">
              What happened with this organisation?
            </Label>
            <div className="flex gap-2">
              <Textarea
                placeholder="E.g., Had a call with the CFO, she wants to see a proposal by Friday..."
                value={interactionText}
                onChange={(e) => setInteractionText(e.target.value)}
                className="min-h-[60px] text-sm"
                data-testid="interaction-input"
              />
              <div className="flex flex-col gap-1 self-end">
                <Button
                  onClick={handleLogInteraction}
                  disabled={loggingInteraction || !interactionText.trim()}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="log-interaction-btn"
                >
                  {loggingInteraction ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.pptx,.txt,.csv,.png,.jpg,.jpeg,.webp"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setUploadingDoc(true);
                      const formData = new FormData();
                      formData.append("file", file);
                      formData.append("client_id", clientId);
                      formData.append("auto_analyze", "true");
                      try {
                        const res = await fetch(`${API}/client-documents/upload`, { method: "POST", credentials: "include", body: formData });
                        const data = await res.json();
                        if (data.success) {
                          toast.success(`${file.name} uploaded & analyzed!`);
                          refreshAllClientData();
                        } else {
                          toast.error(data.error || "Upload failed");
                        }
                      } catch { toast.error("Upload failed"); }
                      finally { setUploadingDoc(false); e.target.value = ""; }
                    }}
                    className="hidden"
                    disabled={uploadingDoc}
                    data-testid="relationship-doc-upload"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full text-slate-500 hover:text-indigo-600 hover:border-indigo-300"
                    disabled={uploadingDoc}
                    asChild
                  >
                    <span>
                      {uploadingDoc ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    </span>
                  </Button>
                </label>
              </div>
            </div>
          </div>

          {/* AUTHORITY: the derived headline (two things + recommendation, eight one click behind).
              Item 19 fold — the old four-dimension grid and narrative were a SECOND authority for
              the same facts and have been removed. What they showed that the headline does not —
              coverage gaps, stakeholder roles, stage mismatch — is carried across below. */}
          <RelationshipHeadline clientId={clientId} embedded />

          {/* Carried across from the old panel: coverage the headline does not cover. */}
          {relationshipInsight && (
            <div className="space-y-3">
              {/* Stage Mismatch Warning */}
              {relationshipInsight.stage_mismatch && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2" data-testid="rel-stage-mismatch">
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <span className="font-medium text-amber-800">Stage Mismatch: </span>
                    <span className="text-amber-700">
                      Manual stage ({relationshipInsight.manual_stage}) differs from observed behaviour ({relationshipInsight.derived_stage})
                    </span>
                  </div>
                </div>
              )}

              {/* Critical Gaps */}
              {relationshipInsight.relationship_state?.critical_gaps?.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3" data-testid="rel-coverage-gaps">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-4 h-4 text-red-500" />
                    <span className="text-sm font-medium text-red-800">Coverage Gaps</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {relationshipInsight.relationship_state.critical_gaps.map((gap, idx) => (
                      <Badge key={idx} variant="outline" className="bg-white text-red-700 border-red-300 text-xs">
                        {gap}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Stakeholder Coverage — the headline names who signs; this is the wider map. */}
              {relationshipInsight.relationship_state?.stakeholders?.length > 0 && (
                <div className="flex flex-wrap items-center gap-2" data-testid="rel-stakeholders">
                  <span className="text-xs text-slate-500">
                    Stakeholders identified ({relationshipInsight.relationship_state.stakeholders.length}):
                  </span>
                  {relationshipInsight.relationship_state.stakeholders.map((role, idx) => (
                    <Badge key={idx} variant="outline" className="bg-green-50 text-green-700 border-green-300 text-xs capitalize">
                      {role.replace("_", " ")}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      )}

      <Tabs defaultValue={defaultTab} className="space-y-4">
        <div className="overflow-x-auto -mx-1 px-1">
          <TabsList className="w-max">
            <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
            <TabsTrigger value="opportunities" data-testid="tab-opportunities">Opportunities</TabsTrigger>
            <TabsTrigger value="notes" data-testid="tab-notes">Notes ({clientNotes.length})</TabsTrigger>
            <TabsTrigger value="documents" data-testid="tab-documents">Documents ({clientDocuments.length})</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts">Contacts</TabsTrigger>
            <TabsTrigger value="email" data-testid="tab-email">Email</TabsTrigger>
            <TabsTrigger value="reply" data-testid="tab-reply">AI Reply</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
          </TabsList>
        </div>

        {/* Details Tab */}
        <TabsContent value="details">
          <Card>
            <CardContent className="pt-6">
              {/* Client Logo Section */}
              <div className="mb-6 pb-6 border-b">
                <Label className="text-slate-500 mb-3 block">Client Logo</Label>
                <div className="flex items-center gap-4">
                  {client.logo_url ? (
                    <div className="relative group">
                      <img
                        src={`${API.replace('/api', '')}${client.logo_url}`}
                        alt={`${client.company_name} logo`}
                        className="w-24 h-24 object-contain rounded-lg border border-slate-200 bg-white p-2"
                      />
                      {canEdit && (
                        <button
                          onClick={handleRemoveLogo}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Remove logo"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="w-24 h-24 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center bg-slate-50">
                      <Image className="w-8 h-8 text-slate-400" />
                    </div>
                  )}
                  {canEdit && (
                    <div>
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                          onChange={handleLogoUpload}
                          className="hidden"
                          disabled={uploadingLogo}
                        />
                        <div className={`flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors ${uploadingLogo ? 'opacity-50' : ''}`}>
                          <Upload className="w-4 h-4 text-slate-600" />
                          <span className="text-sm text-slate-600">
                            {uploadingLogo ? "Uploading..." : client.logo_url ? "Change Logo" : "Upload Logo"}
                          </span>
                        </div>
                      </label>
                      <p className="text-xs text-slate-400 mt-1">PNG, JPG, WEBP, SVG. Max 5MB.</p>
                    </div>
                  )}
                </div>
                {client.logo_url && (
                  <p className="text-xs text-slate-500 mt-2">
                    This logo will be used in all proposals for {client.company_name}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <Label className="text-slate-500">Company Name</Label>
                    {editing ? (
                      <Input
                        value={editData.company_name}
                        onChange={(e) =>
                          setEditData(prev => ({ ...prev, company_name: e.target.value }))
                        }
                        data-testid="edit-company-name"
                      />
                    ) : (
                      <p className="font-medium">{client.company_name}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-slate-500">Engagement Type</Label>
                    {editing ? (
                      <Select
                        value={editData.engagement_type || ""}
                        onValueChange={(v) => setEditData(prev => ({ ...prev, engagement_type: v }))}
                      >
                        <SelectTrigger data-testid="edit-engagement-type">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          {ENGAGEMENT_TYPES.map((t) => (
                            <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p>{client.engagement_type || "-"}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-slate-500">Sector</Label>
                    {editing ? (
                      <Select
                        value={editData.sector || ""}
                        onValueChange={(v) => setEditData(prev => ({ ...prev, sector: v }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select sector" />
                        </SelectTrigger>
                        <SelectContent>
                          {SECTORS.map((s) => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p>{client.sector || "-"}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-slate-500">Tier</Label>
                    {editing ? (
                      <Select
                        value={editData.tier || ""}
                        onValueChange={(v) => setEditData(prev => ({ ...prev, tier: v }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select tier" />
                        </SelectTrigger>
                        <SelectContent>
                          {TIERS.map((t) => (
                            <SelectItem key={t} value={t}>{t}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p>{client.tier || "-"}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-slate-500">Location</Label>
                    {editing ? (
                      <Input
                        value={editData.city || ""}
                        onChange={(e) => setEditData(prev => ({ ...prev, city: e.target.value }))}
                        placeholder="City"
                      />
                    ) : (
                      <p>{[client.city, client.country].filter(Boolean).join(", ") || "-"}</p>
                    )}
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <Label className="text-slate-500">Stage</Label>
                    <p data-testid="client-edit-stage-readonly">{client.stage || "—"}</p>
                    {editing && <p className="text-[10px] text-slate-400">Change a deal's stage from its opportunity status control.</p>}
                    {editing && leaveSignedArmed && SIGNED_WON.includes(client.stage) && !SIGNED_WON.includes(editData.stage) && (
                      <div className="mt-2 rounded border border-amber-300 bg-amber-50 p-2 text-xs" data-testid="clientdetail-leave-signed-confirm">
                        <p className="text-amber-900 font-medium">Move this deal out of {client.stage} → {editData.stage}?</p>
                        <p className="text-amber-800 mt-0.5">Give a reason — recorded in the deal's history with who and when.</p>
                        <textarea className="text-sm border rounded px-2 py-1 w-full mt-1" rows={2} placeholder="Reason for leaving signed…"
                          data-testid="clientdetail-leave-reason" value={leaveSignedReason} onChange={(e) => setLeaveSignedReason(e.target.value)} />
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="text-slate-500">Confidence Level</Label>
                    {editing ? (
                      <Select
                        value={editData.confidence_level || ""}
                        onValueChange={(v) => setEditData(prev => ({ ...prev, confidence_level: v }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select level" />
                        </SelectTrigger>
                        <SelectContent>
                          {CONFIDENCE_LEVELS.map((l) => (
                            <SelectItem key={l} value={l}>{l}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p>{client.confidence_level || "-"}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-slate-500">Potential Offering</Label>
                    {editing ? (
                      <Input
                        value={editData.potential_offering || ""}
                        onChange={(e) => setEditData(prev => ({ ...prev, potential_offering: e.target.value }))}
                      />
                    ) : (
                      <p>{client.potential_offering || "-"}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-slate-500">Account Manager (AM)</Label>
                    {editing ? (
                      client.status === "Dormant" ? (
                        <p className="text-slate-400 italic">N/A (Dormant)</p>
                      ) : (
                        <Select
                          value={editData.account_manager_id || ""}
                          onValueChange={(v) => {
                            const selectedUser = users.find(u => u.user_id === v);
                            setEditData(prev => ({
                              ...prev,
                              account_manager_id: v,
                              account_manager_name: selectedUser?.name || "",
                            }));
                          }}
                        >
                          <SelectTrigger data-testid="account-manager-select">
                            <SelectValue placeholder="Select Account Manager" />
                          </SelectTrigger>
                          <SelectContent>
                            {users.filter(u => u.role !== "viewer" && u.is_active !== false).map((u) => (
                              <SelectItem key={u.user_id} value={u.user_id}>
                                {u.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )
                    ) : (
                      <p>{client.status === "Dormant" ? "N/A" : (client.account_manager_name || "-")}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-slate-500">Deal Owner (DO)</Label>
                    {editing ? (
                      <Select
                        value={editData.deal_owner_id || ""}
                        onValueChange={(v) => {
                          const selectedUser = users.find(u => u.user_id === v);
                          setEditData(prev => ({
                            ...prev,
                            deal_owner_id: v,
                            deal_owner_name: selectedUser?.name || "",
                          }));
                        }}
                      >
                        <SelectTrigger data-testid="deal-owner-select">
                          <SelectValue placeholder="Select Deal Owner" />
                        </SelectTrigger>
                        <SelectContent>
                          {users.filter(u => u.role !== "viewer" && u.is_active !== false).map((u) => (
                            <SelectItem key={u.user_id} value={u.user_id}>
                              {u.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p>{client.deal_owner_name || "-"}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Commercial / Revenue Fields */}
              <div className="mt-6 pt-6 border-t">
                <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-4">Commercial Details</h3>
                {editing && REVENUE_REQUIRED_STAGES.includes(editData.stage) && (editData.engagement_type || "Commercial Opportunity") === "Commercial Opportunity" && (!editData.opportunity_value || !editData.expected_close_date) && (
                  <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2" data-testid="revenue-fields-warning">
                    <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-amber-800">
                      Stage <strong>{editData.stage}</strong> requires <strong>Opportunity Value</strong> and <strong>Expected Close Date</strong> for Commercial Opportunity engagements.
                    </p>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-slate-500">Opportunity Value</Label>
                    <p className="font-medium" data-testid="client-value-readonly">
                      {client.opportunity_value
                        ? new Intl.NumberFormat("en-NG", { style: "currency", currency: client.opportunity_currency || "NGN", maximumFractionDigits: 0 }).format(client.opportunity_value)
                        : "-"}
                    </p>
                    {editing && <p className="text-[11px] text-amber-600 mt-0.5">Set on the opportunity</p>}
                  </div>
                  <div>
                    <Label className="text-slate-500">Currency</Label>
                    <p data-testid="client-currency-readonly">{client.opportunity_currency || "NGN"}</p>
                    {editing && <p className="text-[11px] text-amber-600 mt-0.5">Set on the opportunity</p>}
                  </div>
                  <div>
                    <Label className="text-slate-500">Expected Close Date</Label>
                    <p data-testid="client-close-readonly">{client.expected_close_date ? new Date(client.expected_close_date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "-"}</p>
                    {editing && <p className="text-[11px] text-amber-600 mt-0.5">Set on the opportunity</p>}
                  </div>
                  <div>
                    <Label className="text-slate-500">Actual Revenue</Label>
                    {editing ? (
                      <Input
                        type="number"
                        value={editData.actual_revenue || ""}
                        onChange={(e) => setEditData(prev => ({ ...prev, actual_revenue: e.target.value ? parseFloat(e.target.value) : null }))}
                        placeholder="0"
                        data-testid="edit-actual-revenue"
                      />
                    ) : (
                      <p className="font-medium">
                        {client.actual_revenue
                          ? new Intl.NumberFormat("en-NG", { style: "currency", currency: client.actual_revenue_currency || client.opportunity_currency || "NGN", maximumFractionDigits: 0 }).format(client.actual_revenue)
                          : "-"}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label className="text-slate-500">Revenue Currency</Label>
                    {editing ? (
                      <Select
                        value={editData.actual_revenue_currency || editData.opportunity_currency || "NGN"}
                        onValueChange={(v) => setEditData(prev => ({ ...prev, actual_revenue_currency: v }))}
                      >
                        <SelectTrigger data-testid="edit-actual-revenue-currency">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CURRENCIES.map((c) => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p>{client.actual_revenue_currency || client.opportunity_currency || "NGN"}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-slate-500">Actual Close Date</Label>
                    {editing ? (
                      <Input
                        type="date"
                        value={editData.actual_close_date ? editData.actual_close_date.split("T")[0] : ""}
                        onChange={(e) => setEditData(prev => ({ ...prev, actual_close_date: e.target.value || null }))}
                        data-testid="edit-actual-close-date"
                      />
                    ) : (
                      <p>{client.actual_close_date ? new Date(client.actual_close_date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "-"}</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <Label className="text-slate-500">Notes</Label>
                {editing ? (
                  <Textarea
                    value={editData.tracking_notes || ""}
                    onChange={(e) => setEditData(prev => ({ ...prev, tracking_notes: e.target.value }))}
                    rows={4}
                  />
                ) : (
                  <p className="whitespace-pre-wrap">{client.tracking_notes || "No notes"}</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Opportunities Tab */}
        <TabsContent value="opportunities">
          <OpportunitiesSection
            opportunities={opportunities}
            clientId={clientId}
            canEdit={canEdit}
            users={users}
            user={user}
            focusOppId={focusOppId}
            onRefresh={refreshAllClientData}
            showAddOpp={showAddOpp}
            setShowAddOpp={setShowAddOpp}
            newOpp={newOpp}
            setNewOpp={setNewOpp}
            editingOppId={editingOppId}
            setEditingOppId={setEditingOppId}
            editOppData={editOppData}
            setEditOppData={setEditOppData}
            showAddRevEntry={showAddRevEntry}
            setShowAddRevEntry={setShowAddRevEntry}
            newRevEntry={newRevEntry}
            setNewRevEntry={setNewRevEntry}
          />
        </TabsContent>

        {/* Client Notes Tab */}
        <TabsContent value="notes">
          <Card data-testid="client-notes-section">
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Relationship Notes
              </CardTitle>
              {canEdit && (
                <Button size="sm" variant={showConvForm ? "secondary" : "outline"} onClick={() => setShowConvForm((v) => !v)} data-testid="log-conversation-btn">
                  <MessageSquare className="w-4 h-4 mr-1.5" />{showConvForm ? "Close" : "Log a conversation"}
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Structured conversation form */}
              {canEdit && showConvForm && (
                <div className="rounded-lg border border-purple-200 bg-purple-50/40 p-4 space-y-3" data-testid="conversation-form">
                  <p className="text-xs font-semibold text-purple-700 uppercase tracking-wide">Record a client conversation</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="sm:col-span-2">
                      <Label className="text-xs">Subject</Label>
                      <Input value={conv.subject} onChange={(e) => setConv({ ...conv, subject: e.target.value })} placeholder="e.g. Q3 renewal discussion" data-testid="conv-subject" />
                    </div>
                    <div>
                      <Label className="text-xs">Date</Label>
                      <Input type="date" value={conv.conversation_date} onChange={(e) => setConv({ ...conv, conversation_date: e.target.value })} data-testid="conv-date" />
                    </div>
                    <div>
                      <Label className="text-xs">Time</Label>
                      <Input type="time" value={conv.conversation_time} onChange={(e) => setConv({ ...conv, conversation_time: e.target.value })} data-testid="conv-time" />
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-xs">Participants <span className="text-slate-400">(comma or new line)</span></Label>
                      <Input value={conv.participants} onChange={(e) => setConv({ ...conv, participants: e.target.value })} placeholder="e.g. Remi Dada, Jane (client), John (client)" data-testid="conv-participants" />
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-xs">Agenda</Label>
                      <Textarea value={conv.agenda} onChange={(e) => setConv({ ...conv, agenda: e.target.value })} className="min-h-[50px] text-sm" placeholder="What the conversation set out to cover" data-testid="conv-agenda" />
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-xs">Key points <span className="text-slate-400">(one per line)</span></Label>
                      <Textarea value={conv.key_points} onChange={(e) => setConv({ ...conv, key_points: e.target.value })} className="min-h-[60px] text-sm" placeholder={"Client wants delivery by October\nBudget approved\nNeeds a revised SOW"} data-testid="conv-key-points" />
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-xs">Summary</Label>
                      <Textarea value={conv.summary} onChange={(e) => setConv({ ...conv, summary: e.target.value })} className="min-h-[60px] text-sm" placeholder="A short summary Rex can recall later" data-testid="conv-summary" />
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-xs">Additional notes <span className="text-slate-400">(optional)</span></Label>
                      <Textarea value={conv.content} onChange={(e) => setConv({ ...conv, content: e.target.value })} className="min-h-[50px] text-sm" placeholder="Anything else worth keeping" data-testid="conv-notes" />
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button size="sm" variant="ghost" onClick={() => { setShowConvForm(false); setConv(emptyConv); }}>Cancel</Button>
                    <Button size="sm" onClick={handleAddConversation} disabled={savingConv} data-testid="save-conversation-btn">
                      {savingConv ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}Save conversation
                    </Button>
                  </div>
                </div>
              )}

              {/* Add Note */}
              {canEdit && (
                <div className="flex gap-2" data-testid="add-client-note-form">
                  <Textarea
                    placeholder="Add a note about this client relationship..."
                    value={newClientNote}
                    onChange={(e) => setNewClientNote(e.target.value)}
                    className="min-h-[60px] text-sm"
                    data-testid="new-client-note-input"
                  />
                  <Button
                    size="sm"
                    onClick={handleAddClientNote}
                    disabled={savingNote || !newClientNote.trim()}
                    className="shrink-0 self-end"
                    data-testid="save-client-note-btn"
                  >
                    {savingNote ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  </Button>
                </div>
              )}

              {/* Search / filter */}
              {clientNotes.length > 0 && (
                <div className="relative">
                  <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                  <Input
                    value={noteFilter}
                    onChange={(e) => setNoteFilter(e.target.value)}
                    placeholder="Search by subject, participant, date or text…"
                    className="pl-8 h-9 text-sm"
                    data-testid="notes-search-input"
                  />
                </div>
              )}

              {/* Notes List */}
              {(() => {
                const q = noteFilter.trim().toLowerCase();
                const filteredNotes = !q ? clientNotes : clientNotes.filter((n) => {
                  const hay = [
                    n.content, n.subject, n.summary, n.agenda, n.author_name,
                    n.conversation_date, n.conversation_time,
                    ...(n.participants || []), ...(n.key_points || []),
                  ].filter(Boolean).join(" ").toLowerCase();
                  return hay.includes(q);
                });
                return (
                  <>
                    {clientNotes.length === 0 && (
                      <p className="text-slate-400 text-center py-6 text-sm">No relationship notes yet.</p>
                    )}
                    {clientNotes.length > 0 && filteredNotes.length === 0 && (
                      <p className="text-slate-400 text-center py-6 text-sm" data-testid="notes-no-match">No notes match “{noteFilter}”.</p>
                    )}
                    {filteredNotes.map((note) => (
                <div key={note.note_id} className={`border rounded-lg p-3 space-y-1 ${note.auto_generated ? "border-indigo-100 bg-indigo-50/30" : "border-slate-200"}`} data-testid={`note-${note.note_id}`}>
                  {editingNoteId === note.note_id ? (
                    <div className="space-y-2">
                      <Textarea
                        value={editNoteContent}
                        onChange={(e) => setEditNoteContent(e.target.value)}
                        className="min-h-[60px] text-sm"
                      />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => handleUpdateNote(note.note_id)}>
                          <Save className="w-3 h-3 mr-1" /> Save
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingNoteId(null)}>Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {note.note_type === "conversation" ? (
                        <div className="space-y-1.5" data-testid={`conversation-${note.note_id}`}>
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-purple-200 text-purple-700 bg-purple-50">Conversation</Badge>
                            <span className="text-sm font-semibold text-slate-800">{note.subject || "(no subject)"}</span>
                            {(note.conversation_date || note.conversation_time) && (
                              <span className="text-[11px] text-slate-500">{note.conversation_date}{note.conversation_time ? ` · ${note.conversation_time}` : ""}</span>
                            )}
                          </div>
                          {note.participants?.length > 0 && (
                            <p className="text-xs text-slate-600"><span className="font-medium text-slate-500">Participants:</span> {note.participants.join(", ")}</p>
                          )}
                          {note.agenda && (
                            <p className="text-xs text-slate-600"><span className="font-medium text-slate-500">Agenda:</span> {note.agenda}</p>
                          )}
                          {note.key_points?.length > 0 && (
                            <div className="text-xs text-slate-600">
                              <span className="font-medium text-slate-500">Key points:</span>
                              <ul className="list-disc ml-5 mt-0.5">{note.key_points.map((k, i) => <li key={i}>{k}</li>)}</ul>
                            </div>
                          )}
                          {note.summary && (
                            <p className="text-sm text-slate-700 whitespace-pre-wrap"><span className="font-medium text-slate-500 text-xs">Summary: </span>{note.summary}</p>
                          )}
                          {note.content && <p className="text-sm text-slate-700 whitespace-pre-wrap">{note.content}</p>}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-700 whitespace-pre-wrap">{note.content}</p>
                      )}
                      <div className="flex items-center justify-between text-xs text-slate-400 pt-1">
                        <div className="flex items-center gap-2">
                          <span>{note.author_name} &middot; {new Date(note.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                          {note.auto_generated && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-indigo-200 text-indigo-600 bg-indigo-50">
                              {note.source === "rex" ? "Rex" : note.source === "document" ? "Document" : "Auto"}
                            </Badge>
                          )}
                        </div>
                        {canEdit && (
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => { setEditingNoteId(note.note_id); setEditNoteContent(note.content); }}>
                              <Edit2 className="w-3 h-3" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-400 hover:text-red-600" onClick={() => handleDeleteNote(note.note_id)}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ))}
                  </>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents">
          <DocumentsSection
            documents={clientDocuments}
            clientId={clientId}
            onRefresh={refreshAllClientData}
            canEdit={canEdit}
          />
        </TabsContent>

        {/* Contacts Tab */}
        <TabsContent value="contacts">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Contacts</CardTitle>
              {canEdit && (
                <Button
                  size="sm"
                  onClick={() => setShowAddContact(true)}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="add-contact-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Contact
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {/* Add Contact Form */}
              {showAddContact && (
                <div className="mb-6 p-4 border border-blue-200 rounded-lg bg-blue-50">
                  <h4 className="font-medium mb-3 text-blue-900">Add New Contact</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Name</Label>
                      <Input
                        value={newContact.name}
                        onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                        placeholder="Contact name"
                        data-testid="new-contact-name"
                      />
                    </div>
                    <div>
                      <Label>Title</Label>
                      <Input
                        value={newContact.title}
                        onChange={(e) => setNewContact({ ...newContact, title: e.target.value })}
                        placeholder="Job title"
                        data-testid="new-contact-title"
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={newContact.email}
                        onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                        placeholder="email@company.com"
                        data-testid="new-contact-email"
                      />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input
                        value={newContact.phone}
                        onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                        placeholder="+1 234 567 8900"
                        data-testid="new-contact-phone"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button
                      size="sm"
                      onClick={handleAddContact}
                      disabled={savingContact}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="save-new-contact-btn"
                    >
                      {savingContact ? "Saving..." : <><Save className="w-4 h-4 mr-1" /> Save Contact</>}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setShowAddContact(false);
                        setNewContact({ name: "", email: "", phone: "", title: "" });
                      }}
                    >
                      <X className="w-4 h-4 mr-1" /> Cancel
                    </Button>
                  </div>
                </div>
              )}

              {client.contacts?.length > 0 ? (
                <div className="space-y-4">
                  {client.contacts.map((contact, idx) => (
                    <div key={idx} className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg" data-testid={`contact-card-${idx}`}>
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-blue-600" />
                      </div>
                      
                      {editingContactIdx === idx ? (
                        /* Edit Contact Form */
                        <div className="flex-1 space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs">Name</Label>
                              <Input
                                value={editContactData.name}
                                onChange={(e) => setEditContactData({ ...editContactData, name: e.target.value })}
                                placeholder="Contact name"
                                data-testid={`edit-contact-name-${idx}`}
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Title</Label>
                              <Input
                                value={editContactData.title}
                                onChange={(e) => setEditContactData({ ...editContactData, title: e.target.value })}
                                placeholder="Job title"
                                data-testid={`edit-contact-title-${idx}`}
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Email</Label>
                              <Input
                                type="email"
                                value={editContactData.email}
                                onChange={(e) => setEditContactData({ ...editContactData, email: e.target.value })}
                                placeholder="email@company.com"
                                data-testid={`edit-contact-email-${idx}`}
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Phone</Label>
                              <Input
                                value={editContactData.phone}
                                onChange={(e) => setEditContactData({ ...editContactData, phone: e.target.value })}
                                placeholder="+1 234 567 8900"
                                data-testid={`edit-contact-phone-${idx}`}
                              />
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={handleSaveContactEdit}
                              disabled={savingContact}
                              className="bg-emerald-600 hover:bg-emerald-700"
                              data-testid={`save-contact-edit-${idx}`}
                            >
                              {savingContact ? "Saving..." : <><Save className="w-3 h-3 mr-1" /> Save</>}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingContactIdx(null);
                                setEditContactData({ name: "", email: "", phone: "", title: "" });
                              }}
                            >
                              <X className="w-3 h-3 mr-1" /> Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        /* Display Contact */
                        <>
                          <div className="flex-1">
                            <p className="font-medium">{contact.name || "Unknown"}</p>
                            <p className="text-sm text-slate-500">{contact.title || ""}</p>
                            <div className="flex flex-wrap gap-4 mt-2 text-sm">
                              {contact.email && (
                                <a href={`mailto:${contact.email}`} className="flex items-center gap-1 text-blue-600 hover:underline">
                                  <Mail className="w-4 h-4" />
                                  {contact.email}
                                </a>
                              )}
                              {contact.phone && (
                                <span className="flex items-center gap-1 text-slate-600">
                                  <Phone className="w-4 h-4" />
                                  {contact.phone}
                                </span>
                              )}
                            </div>
                          </div>
                          {canEdit && (
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleEditContact(idx)}
                                className="text-slate-500 hover:text-blue-600"
                                data-testid={`edit-contact-btn-${idx}`}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  if (window.confirm("Are you sure you want to delete this contact?")) {
                                    handleDeleteContact(idx);
                                  }
                                }}
                                className="text-slate-500 hover:text-red-600"
                                data-testid={`delete-contact-btn-${idx}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">
                  No contacts added yet.
                  {canEdit && !showAddContact && (
                    <Button
                      variant="link"
                      onClick={() => setShowAddContact(true)}
                      className="text-blue-600 ml-2"
                    >
                      Add your first contact
                    </Button>
                  )}
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email Tab */}
        <TabsContent value="email">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Send Email
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* AI Generation */}
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-5 h-5 text-purple-600" />
                  <span className="font-medium text-purple-900">AI-Powered Email Generation</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleGenerateAI("first_contact")} disabled={generatingAI} className="bg-white">
                    First Contact
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleGenerateAI("follow_up")} disabled={generatingAI} className="bg-white">
                    Follow-up
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleGenerateAI("meeting_request")} disabled={generatingAI} className="bg-white">
                    Meeting Request
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleGenerateAI("re_engagement")} disabled={generatingAI} className="bg-white">
                    Re-engagement
                  </Button>
                </div>
                {generatingAI && (
                  <p className="text-sm text-purple-600 mt-2 flex items-center gap-2">
                    <span className="animate-spin">⏳</span> Generating personalised email...
                  </p>
                )}
              </div>

              {/* Template Selection */}
              <div>
                <Label>Or use a template</Label>
                <Select value={selectedTemplate} onValueChange={handleTemplateSelect}>
                  <SelectTrigger data-testid="email-template-select">
                    <SelectValue placeholder="Select a template" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => (
                      <SelectItem key={t.template_id} value={t.template_id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Recipient */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>To Name</Label>
                  <Input value={toName} onChange={(e) => setToName(e.target.value)} placeholder="Recipient name" data-testid="email-to-name" />
                </div>
                <div>
                  <Label>To Email</Label>
                  <Input type="email" value={toEmail} onChange={(e) => setToEmail(e.target.value)} placeholder="recipient@company.com" data-testid="email-to-email" />
                </div>
              </div>

              {/* Subject */}
              <div>
                <Label>Subject</Label>
                <Input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} placeholder="Email subject" data-testid="email-subject" />
              </div>

              {/* Body */}
              <div>
                <Label>Body</Label>
                <Textarea value={emailBody} onChange={(e) => setEmailBody(e.target.value)} placeholder="Write your email content..." rows={10} className="font-sans" data-testid="email-body" />
              </div>

              {/* Suggested Attachments */}
              {suggestedAttachments.length > 0 && (
                <div className="bg-amber-50 rounded-lg p-3">
                  <p className="text-sm font-medium text-amber-800 flex items-center gap-2">
                    <Paperclip className="w-4 h-4" />
                    Suggested Attachments
                  </p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {suggestedAttachments.map((doc) => (
                      <Badge key={doc.document_id} variant="secondary" className="bg-white">
                        {doc.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end">
                <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleSendEmail} disabled={sendingEmail || !toEmail || !emailSubject || !emailBody} data-testid="send-email-btn">
                  {sendingEmail ? "Sending..." : <><Send className="w-4 h-4 mr-2" /> Send Email</>}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Reply Tab */}
        <TabsContent value="reply">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Reply className="w-5 h-5" />
                AI Reply Assistant
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-gradient-to-r from-emerald-50 to-blue-50 rounded-lg p-4">
                <p className="text-sm text-slate-600 mb-2">
                  <strong>How it works:</strong> Paste the client&apos;s response below. AI will analyse it against your knowledge base and conversation history to generate a smart, contextual reply.
                </p>
              </div>

              <div>
                <Label>Client&apos;s Response (paste here)</Label>
                <Textarea
                  value={clientResponse}
                  onChange={(e) => setClientResponse(e.target.value)}
                  placeholder="Paste the email response you received from the client..."
                  rows={6}
                  data-testid="client-response-input"
                />
              </div>

              <Button
                onClick={handleGenerateReply}
                disabled={generatingReply || !clientResponse.trim()}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                data-testid="generate-reply-btn"
              >
                {generatingReply ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    Generating Reply...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate AI Reply
                  </>
                )}
              </Button>

              {emailBody && (
                <>
                  <div className="border-t pt-4 mt-4">
                    <Label>Generated Reply (edit as needed)</Label>
                    <Input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} placeholder="Subject" className="mb-2" />
                    <Textarea value={emailBody} onChange={(e) => setEmailBody(e.target.value)} rows={10} className="font-sans" />
                  </div>

                  {suggestedAttachments.length > 0 && (
                    <div className="bg-amber-50 rounded-lg p-3">
                      <p className="text-sm font-medium text-amber-800 flex items-center gap-2">
                        <Paperclip className="w-4 h-4" />
                        Suggested Attachments
                      </p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {suggestedAttachments.map((doc) => (
                          <Badge key={doc.document_id} variant="secondary" className="bg-white">
                            {doc.name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end">
                    <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleSendEmail} disabled={sendingEmail || !toEmail || !emailSubject || !emailBody} data-testid="send-reply-btn">
                      {sendingEmail ? "Sending..." : <><Send className="w-4 h-4 mr-2" /> Send Reply</>}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Engagement History</CardTitle>
            </CardHeader>
            <CardContent>
              {engagements.length > 0 ? (
                <div className="space-y-4">
                  {engagements.map((eng) => (
                    <div key={eng.engagement_id} className="flex items-start gap-4 p-4 border border-slate-100 rounded-lg">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        eng.engagement_type === "email" ? "bg-blue-100" :
                        eng.engagement_type === "call" ? "bg-emerald-100" :
                        eng.engagement_type === "meeting" ? "bg-purple-100" : "bg-slate-100"
                      }`}>
                        {eng.engagement_type === "email" ? <Mail className="w-5 h-5 text-blue-600" /> :
                         eng.engagement_type === "call" ? <Phone className="w-5 h-5 text-emerald-600" /> :
                         eng.engagement_type === "meeting" ? <Calendar className="w-5 h-5 text-purple-600" /> :
                         <MessageSquare className="w-5 h-5 text-slate-600" />}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="font-medium capitalize">
                            {eng.engagement_type}{eng.subject && `: ${eng.subject}`}
                          </p>
                          <span className="text-sm text-slate-400 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {new Date(eng.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-sm text-slate-500 mt-1">By {eng.user_name}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">No engagement history yet</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
